// src/pages/CardReportingPage.js

import React, { useState, useEffect, useMemo } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button
} from '@mui/material'
import DvrIcon from '@mui/icons-material/Dvr';
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil
import { useTranslation } from 'react-i18next'
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';


export default function CardReportingPage() {
  const { currentUser, userRole } = useAuth()
  const { t } = useTranslation()

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [workspaces, setWorkspaces] = useState([])
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const [workspaceLoading, setWorkspaceLoading] = useState(true)

  const [channels, setChannels] = useState([])
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [channelsLoading, setChannelsLoading] = useState(false)

  const [questionForms, setQuestionForms] = useState([])
  const [selectedForm, setSelectedForm] = useState(null)
  const [formsLoading, setFormsLoading] = useState(false)

  const [availableTags, setAvailableTags] = useState([])
  const [selectedTags, setSelectedTags] = useState([])

  const [rawRows, setRawRows] = useState([])
  const [loadingAnswers, setLoadingAnswers] = useState(false)

  const [usersMap, setUsersMap] = useState({})

  const [downloading, setDownloading] = useState(false)

  const [availableQuestions, setAvailableQuestions] = useState([])
  const [selectedQuestions, setSelectedQuestions] = useState([])

  // Yeni: Kullanıcı arama state'i
  const [userSearch, setUserSearch] = useState('')

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  // --------------------------------------------------------------------------
  // HELPER FUNCTIONS
  // --------------------------------------------------------------------------
  const stripHtmlTags = (htmlString = '') => {
    const div = document.createElement('div')
    div.innerHTML = htmlString
    return div.textContent || div.innerText || ''
  }

  const isLikelyImageUrl = (str) => {
    if (typeof str !== 'string') return false
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(str)
  }

  const isLikelyVideoUrl = (str) => {
    if (typeof str !== 'string') return false
    return (
      /\.(mp4|webm|mov|avi|mkv)$/i.test(str) ||
      str.includes('firebasestorage.googleapis.com')
    )
  }

  // --------------------------------------------------------------------------
  // 0) Tüm kullanıcıları çek -> usersMap (userId -> userEmail)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const userSnap = await getDocs(collection(db, 'users'))
        const tempMap = {}
        userSnap.forEach((docSnap) => {
          const data = docSnap.data()
          tempMap[docSnap.id] = data.nickname || docSnap.id
        })
        setUsersMap(tempMap)
      } catch (error) {
        console.error('users alınırken hata:', error)
      }
    }
    fetchAllUsers()
  }, [])

  // --------------------------------------------------------------------------
  // 1) Workspace listesi (live onSnapshot)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const wsRef = collection(db, 'workspaces')
    const unsubscribe = onSnapshot(
      wsRef,
      async (snapshot) => {
        try {
          let wsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))

          if (userRole === 'admin') {
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          } else {
            const allWsIds = wsData.map((ws) => ws.id)
            if (allWsIds.length === 0) {
              setWorkspaces([])
              setWorkspaceLoading(false)
              return
            }

            const channelsRef = collection(db, 'channels')
            const qCh = query(channelsRef, where('workspaceId', 'in', allWsIds))
            const chSnap = await getDocs(qCh)

            const relevantWsIds = new Set()
            chSnap.forEach((chDoc) => {
              const chData = chDoc.data()
              if (
                chData.memberDetails &&
                chData.memberDetails.some(
                  (md) => md.userId === currentUser.uid && md.role === 'moderator'
                )
              ) {
                relevantWsIds.add(chData.workspaceId)
              }
            })

            wsData = wsData.filter((ws) => relevantWsIds.has(ws.id))
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          }
        } catch (err) {
          console.error('Workspace okunurken hata:', err)
          setSnackbar({
            open: true,
            message: t('cardReporting.workspaceLoadingError'),
            severity: 'error'
          })
          setWorkspaceLoading(false)
        }
      },
      (err) => {
        console.error('Workspace okunirken hata (snapshot):', err)
        setSnackbar({
          open: true,
          message: t('cardReporting.workspaceSnapshotError'),
          severity: 'error'
        })
        setWorkspaceLoading(false)
      }
    )

    return () => unsubscribe()
  }, [userRole, currentUser, t])

  // --------------------------------------------------------------------------
  // 2) Workspace seçilince -> kanal listesini çek
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedWorkspace) {
      setChannels([])
      setSelectedChannel(null)
      return
    }

    const fetchChannels = async () => {
      try {
        setChannelsLoading(true)
        const chRef = collection(db, 'channels')
        const qChannel = query(chRef, where('workspaceId', '==', selectedWorkspace.id))
        const chSnap = await getDocs(qChannel)
        const allChannels = chSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        const modChannels = allChannels.filter((ch) => {
          if (!ch.memberDetails) return false
          return ch.memberDetails.some(
            (md) => md.userId === currentUser.uid && md.role === 'moderator'
          )
        })

        setChannels(modChannels)
        setChannelsLoading(false)
      } catch (err) {
        console.error('Kanal listesi alınırken hata:', err)
        setSnackbar({
          open: true,
          message: t('cardReporting.channelListError'),
          severity: 'error'
        })
        setChannelsLoading(false)
      }
    }

    setAvailableTags(selectedWorkspace.tags || [])
    setSelectedTags([])
    fetchChannels()
  }, [selectedWorkspace, currentUser, t])

  // --------------------------------------------------------------------------
  // 3) Seçilen kanal için form listesini çek
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedChannel) {
      setQuestionForms([])
      setSelectedForm(null)
      return
    }

    const fetchForms = async () => {
      try {
        setFormsLoading(true)
        const ref = collection(db, 'questionForms')
        const qForms = query(ref, where('channelId', '==', selectedChannel.id))
        const formsSnap = await getDocs(qForms)
        const formsData = formsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setQuestionForms(formsData)
        setFormsLoading(false)
      } catch (error) {
        console.error('Form listesi alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('cardReporting.formListError'),
          severity: 'error'
        })
        setFormsLoading(false)
      }
    }

    fetchForms()
  }, [selectedChannel, t])

  // --------------------------------------------------------------------------
  // 4) Seçilen formun cevaplarını çek (ÖNEMLİ: card sorting)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedForm) {
      setRawRows([])
      return
    }

    const fetchAnswers = async () => {
      try {
        setLoadingAnswers(true)
        const respRef = collection(db, 'responses')
        const qResp = query(
          respRef,
          where('channelId', '==', selectedChannel.id),
          where('questionFormId', '==', selectedForm.id)
        )
        const respSnap = await getDocs(qResp)
        const rawResponses = respSnap.docs.map((docSnap) => ({
          docId: docSnap.id,
          ...docSnap.data()
        }))

        // Soruları çekerek kart sıralama için kart ve grup bilgilerini al
        const questionsRef = collection(db, "questions");
        const qQuery = query(
          questionsRef,
          where("questionFormId", "==", selectedForm.id)
        );
        const questionsSnap = await getDocs(qQuery);
        const questionsMap = {};
        questionsSnap.forEach((doc) => {
          questionsMap[doc.id] = doc.data();
        });

        const userTagMap = {}
        selectedChannel.memberDetails?.forEach((md) => {
          userTagMap[md.userId] = md.tags || []
        })

        const allRows = []
        rawResponses.forEach((resp) => {
          const { userId, finalAnswers, completedAt } = resp
          const userEmail = usersMap[userId] || userId
          const userTags = userTagMap[userId] || []
          const dateStr = completedAt?.toDate
            ? completedAt.toDate().toLocaleString()
            : ''

          if (!Array.isArray(finalAnswers)) return

          finalAnswers.forEach((fa, idx) => {
            let textAnswer = ''
            let mediaList = []
            let videoUrl = ''

            if (typeof fa.answer === 'object' && fa.answer !== null) {
              if ( fa.answer.hasOwnProperty('unsorted') ) {
                const groupLines = []
                const localMedia = []
            
                // Object.entries: Örn "Grup 1" => [ { text, imageUrl }, ... ]
                Object.entries(fa.answer).forEach(([groupName, cardsArray]) => {
                  // Örnek: groupName "unsorted" olduğunda ekrana "unsorted" yazmak istemezseniz,
                  // burada farklı bir koşulla atlayabilirsiniz. (Örneğin if (groupName === 'unsorted' && !cardsArray.length) return ...)
                  if (!Array.isArray(cardsArray)) return
            
                  // Kartların text + imageUrl'lerini işleyelim
                  const cardTexts = cardsArray.map((cardObj) => {
                    let cText = cardObj.text || ''                
                    return cText
                  })
            
                  if (cardsArray.length > 0) {
                    groupLines.push(`${groupName}: ${cardTexts.join(', ')}`)
                  }
                })
            
                // Grupları <br/> ile birleştirip textAnswer’a koyuyoruz
                textAnswer = groupLines.join('<br/>')
            
                // Bulunan tüm resimleri mediaList’e atadık
                mediaList = localMedia

              } else if (fa.answer.media && Array.isArray(fa.answer.media)) {
                // openEnded tipi: hem text hem media
                if (fa.answer.text) textAnswer = fa.answer.text
                mediaList = fa.answer.media.map((mObj) => ({
                  type: mObj.fileType || 'image',
                  url: mObj.remoteUrl
                }))
              } else if (fa.answer.videoUrl) {
                // videoQuestion tipi
                videoUrl = fa.answer.videoUrl
              } else if (fa.answer.collageSingleUrl) {       
                // photoCollage
                mediaList = [{ type: 'image', url: fa.answer.collageSingleUrl }]
              } else if (Array.isArray(fa.answer)) {      
                // AI-Moderator
                  if (
                    fa.answer.length > 0 &&
                    fa.answer.every(
                      (msgObj) =>
                        msgObj &&
                        typeof msgObj === 'object' &&
                        'role' in msgObj &&
                        'content' in msgObj
                    )
                  ) {
                    // Chat benzeri format -> role + content
                    textAnswer = fa.answer
                      .map((msgObj) => `[${msgObj.role}] ${msgObj.content}`)
                      .join('<br/>')
                } else {
                  // Çoklu string array (multipleChoice)
                  textAnswer = fa.answer.join(', ')
                }
              } else if (typeof fa.answer.text === 'string') {
                // openEnded sadece text
                textAnswer = fa.answer.text
              }               
            } else if (Array.isArray(fa.answer)) {   
              // multipleChoice ya da AI as array of strings
              textAnswer = fa.answer.join(', ')
            } else if (typeof fa.answer === 'string') {
              // singleChoice, vs
              textAnswer = fa.answer
            } else if (typeof fa.answer === 'number') {
              // rating
              textAnswer = String(fa.answer)
            } 

            allRows.push({
              id: resp.docId + '_' + idx,
              docId: resp.docId,
              userId: userId,
              userEmail,
              userTags,
              questionText: fa.questionText,
              textAnswer,
              mediaList,
              videoUrl,
              createdAt: dateStr
            })
          })
        })

        setRawRows(allRows)
        setLoadingAnswers(false)
      } catch (error) {
        console.error('Cevaplar alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('cardReporting.answersLoadingError'),
          severity: 'error'
        })
        setLoadingAnswers(false)
      }
    }

    fetchAnswers()
  }, [selectedForm, selectedChannel, usersMap, t])

  // --------------------------------------------------------------------------
  // Filtre: Seçili tag'lere göre filtre
  // --------------------------------------------------------------------------
  const filteredRawRows = useMemo(() => {
    let filtered = rawRows

    // Tag filtre
    if (selectedTags.length > 0) {
      filtered = filtered.filter((r) => {
        return selectedTags.some((tag) => r.userTags.includes(tag))
      })
    }

    // Soru filtre
    if (selectedQuestions.length > 0) {
      filtered = filtered.filter((r) => {
        return selectedQuestions.includes(r.questionText)
      })
    }

    // Sıralamayı, availableQuestions sırasına göre yap
    filtered = filtered.sort((a, b) => {
      const aIndex = availableQuestions.indexOf(a.questionText)
      const bIndex = availableQuestions.indexOf(b.questionText)
      return aIndex - bIndex
    })

    return filtered
  }, [rawRows, selectedTags, selectedQuestions, availableQuestions])

  // --------------------------------------------------------------------------
  // Kullanıcı bazında grupla
  // --------------------------------------------------------------------------
  const groupedByUser = useMemo(() => {
    const userMap = {}
    filteredRawRows.forEach((r) => {
      const userKey = r.userEmail
      if (!userMap[userKey]) {
        userMap[userKey] = {
          userEmail: r.userEmail,
          userTags: r.userTags,
          responseDocId: r.docId,
          answers: [],
          createdAtList: []
        }
      }
      userMap[userKey].answers.push({
        questionText: r.questionText,
        textAnswer: r.textAnswer,
        mediaList: r.mediaList,
        videoUrl: r.videoUrl
      })
      userMap[userKey].createdAtList.push(r.createdAt)
    })

    return Object.values(userMap).map((obj) => {
      const earliestDate = obj.createdAtList[0] || ''
      return {
        ...obj,
        answers: obj.answers.sort((a, b) => {
          const aIndex = availableQuestions.indexOf(a.questionText)
          const bIndex = availableQuestions.indexOf(b.questionText)
          return aIndex - bIndex
        }),
        displayDate: earliestDate
      }
    })
  }, [filteredRawRows, availableQuestions])

  // Yeni: Kullanıcı arama sonuçlarını filtrele (e-posta bazında)
  const finalUserGroups = useMemo(() => {
    if (!userSearch) return groupedByUser
    return groupedByUser.filter((user) =>
      user.userEmail.toLowerCase().includes(userSearch.toLowerCase())
    )
  }, [groupedByUser, userSearch])

  // --------------------------------------------------------------------------
  // "Reddet" Butonu (Örnek: Yanıtı tekrar 'inProgress'e çekmek vb.)
  // --------------------------------------------------------------------------
  const handleReject = async (responseDocId) => {
    if (!responseDocId) return
    try {
      await updateDoc(doc(db, 'responses', responseDocId), {
        status: 'inProgress',
        progress: 0,
        rejected: true // sadece bir flag
      })

      setRawRows((prev) => prev.filter((row) => row.docId !== responseDocId))

      setSnackbar({
        open: true,
        message: t('cardReporting.rejectSuccess'),
        severity: 'success'
      })
    } catch (err) {
      console.error('Reddetme işleminde hata:', err)
      setSnackbar({
        open: true,
        message: t('cardReporting.rejectError'),
        severity: 'error'
      })
    }
  }

  // --------------------------------------------------------------------------
  // Seçim kontrol
  // --------------------------------------------------------------------------
  const getSafeValue = (id, options) => {
    if (!id) return ''
    if (options.some((opt) => opt.id === id)) {
      return id
    }
    return ''
  }

  // --------------------------------------------------------------------------
  // File download yardımı
  // --------------------------------------------------------------------------
  const sanitizeFileName = (name) => {
    return name.replace(/[^a-z0-9]/gi, '_')
  }

  const getFileExtension = (url, defaultType) => {
    const match = url.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
    if (match && match[1]) {
      return match[1]
    }
    if (defaultType === 'image') return 'jpg'
    if (defaultType === 'video') return 'mp4'
    return ''
  }

  async function downloadWithCustomName(url, fileName) {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const blob = await response.blob()
      const objUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = objUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(objUrl)
    } catch (error) {
      console.error('Dosya indirilemedi:', error)
    }
  }

  // --------------------------------------------------------------------------
  // Excel'e Aktar
  // --------------------------------------------------------------------------
  const handleExportToExcel = () => {
    if (finalUserGroups.length === 0) {
      setSnackbar({
        open: true,
        message: t('cardReporting.noDataToExport'),
        severity: 'warning'
      });
      return;
    }

    // Flatten the data structure for Excel
    const excelData = finalUserGroups.flatMap(user => 
      user.answers.map((answer, index) => ({
        user: user.userEmail,
        tags: user.userTags.join(', '),
        question: stripHtmlTags(answer.questionText),
        answer: stripHtmlTags(answer.textAnswer),
        media: answer.mediaList.map(m => m.url).join('\n'),
        video: answer.videoUrl,
        date: user.displayDate
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Responses");
    XLSX.writeFile(workbook, "UserResponses.xlsx");
  };

  // DataGrid için kolonlar
  const columns = [
    { 
      field: 'userEmail', 
      headerName: t('cardReporting.userEmail'), 
      flex: 1 
    },
    { 
      field: 'userTags', 
      headerName: t('cardReporting.tags'), 
      flex: 1,
      renderCell: (params) => params.value.join(', ')
    },
    { 
      field: 'questionText', 
      headerName: t('cardReporting.question'), 
      flex: 2,
      renderCell: (params) => {
        const div = document.createElement('div');
        div.innerHTML = params.value || '';
        return div.textContent || '';
      }
    },
    { 
      field: 'textAnswer', 
      headerName: t('cardReporting.answer'), 
      flex: 2,
      renderCell: (params) => {
        // HTML içeriğini satır bazında göstermek için <br/> -> newline
        const formattedText = params.value?.replace(/<br\/>/g, '\n') || '';
        return (
          <div style={{ whiteSpace: 'pre-line' }}>
            {formattedText}
          </div>
        );
      }
    },
    { 
      field: 'media', 
      headerName: t('cardReporting.media'), 
      flex: 2,
      renderCell: (params) => params.row.mediaList?.map(m => m.url).join('\n') 
    },
    { 
      field: 'videoUrl', 
      headerName: t('cardReporting.video'), 
      flex: 1 
    },
    { 
      field: 'createdAt', 
      headerName: t('cardReporting.date'), 
      flex: 1 
    },
  ];

  // DataGrid row'ları
  const dataGridRows = useMemo(() => 
    finalUserGroups.flatMap(user => 
      user.answers.map((answer, index) => ({
        id: `${user.userEmail}-${index}`,
        userEmail: user.userEmail,
        userTags: user.userTags,
        questionText: answer.questionText,
        textAnswer: answer.textAnswer,
        mediaList: answer.mediaList,
        videoUrl: answer.videoUrl,
        createdAt: user.displayDate
      }))
    ),
    [finalUserGroups]
  );

  // DataGrid Custom Toolbar
  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbar />
      <Button 
        startIcon={<FileDownloadIcon />}
        onClick={handleExportToExcel}
        sx={{ ml: 2 }}
      >
        {t('cardReporting.exportExcel')}
      </Button>
    </GridToolbarContainer>
  );

  // 5) Seçilen form için soruları çek (Soru ismi filtresi için)
  useEffect(() => {
    if (!selectedForm) {
      setAvailableQuestions([])
      return
    }
  
    const fetchQuestions = async () => {
      try {
        const q = query(
          collection(db, 'questions'),
          where('questionFormId', '==', selectedForm.id)
        )
        const querySnapshot = await getDocs(q)
        
        // Soruları sortOrder'a göre sırala
        const questions = querySnapshot.docs
          .map(doc => ({
            ...doc.data(),
            id: doc.id
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder)
  
        // Tekilleştirme yapmadan doğrudan sıralı listeyi al
        const questionTexts = questions.map(q => q.text)
        setAvailableQuestions(questionTexts)
      } catch (error) {
        console.error('Sorular alınırken hata:', error)
      }
    }
  
    fetchQuestions()
  }, [selectedForm])

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Container
      sx={{
        my: 1,
        mt: 4,
        padding: 4,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: 1
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <DvrIcon sx={{ mr: 1 }} />
        {t('cardReporting.pageTitle')}
      </Typography>

      {/* Workspace Seçimi */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1,
          width: '100%',
          maxWidth: 900,
          backgroundColor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('cardReporting.workspaceSectionTitle')}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="workspace-select-label">{t('cardReporting.workspaceLabel')}</InputLabel>
          <Select
            labelId="workspace-select-label"
            label={t('cardReporting.workspaceLabel')}
            value={getSafeValue(selectedWorkspace?.id, workspaces)}
            onChange={(e) => {
              const wsId = e.target.value
              const wsObj = workspaces.find((w) => w.id === wsId) || null
              setSelectedWorkspace(wsObj)
              setSelectedChannel(null)
              setSelectedForm(null)
            }}
          >
            <MenuItem value="">{t('cardReporting.select')}</MenuItem>
            {workspaces.map((ws) => (
              <MenuItem key={ws.id} value={ws.id}>
                {ws.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {selectedWorkspace && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1,
            width: '100%',
            maxWidth: 900,
            backgroundColor: '#fff',
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('cardReporting.channelFormFilters')}
          </Typography>
          <Stack spacing={1}>
            {/* Kanal Seçimi */}
            <FormControl fullWidth size="small">
              <InputLabel id="channel-select-label">{t('cardReporting.channelSelectLabel')}</InputLabel>
              <Select
                labelId="channel-select-label"
                label={t('cardReporting.channelSelectLabel')}
                value={getSafeValue(selectedChannel?.id, channels)}
                onChange={(e) => {
                  const chId = e.target.value
                  const chObj = channels.find((c) => c.id === chId) || null
                  setSelectedChannel(chObj)
                  setSelectedForm(null)
                }}
              >
                <MenuItem value="">{t('cardReporting.selectChannel')}</MenuItem>
                {channelsLoading ? (
                  <MenuItem disabled>{t('cardReporting.channelsLoading')}</MenuItem>
                ) : (
                  channels.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Form Seçimi */}
            {selectedChannel && (
              <FormControl fullWidth size="small">
                <InputLabel id="form-select-label">{t('cardReporting.formSelectLabel')}</InputLabel>
                <Select
                  labelId="form-select-label"
                  label={t('cardReporting.formSelectLabel')}
                  value={getSafeValue(selectedForm?.id, questionForms)}
                  onChange={(e) => {
                    const fId = e.target.value
                    const fObj = questionForms.find((f) => f.id === fId) || null
                    setSelectedForm(fObj)
                  }}
                >
                  <MenuItem value="">{t('cardReporting.selectForm')}</MenuItem>
                  {formsLoading ? (
                    <MenuItem disabled>{t('cardReporting.formsLoading')}</MenuItem>
                  ) : (
                    questionForms.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Soru Filtresi */}
            {availableQuestions.length > 0 && (
              <Autocomplete
                multiple
                options={availableQuestions}
                value={selectedQuestions}
                onChange={(event, newValue) => {
                  // Seçilenleri availableQuestions sırasına göre sırala
                  const sorted = newValue.slice().sort((a, b) => 
                    availableQuestions.indexOf(a) - availableQuestions.indexOf(b)
                  )
                  setSelectedQuestions(sorted)
                }}
                getOptionLabel={(option) => stripHtmlTags(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('cardReporting.filterByQuestions')}
                    placeholder={t('cardReporting.selectQuestions')}
                    size="small"
                  />
                )}
              />
            )}

            {/* Tag Filtre */}
            {selectedChannel && availableTags.length > 0 && (
              <Autocomplete
                multiple
                options={availableTags}
                value={selectedTags}
                onChange={(event, newValue) => setSelectedTags(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('cardReporting.userTags')}
                    placeholder={t('cardReporting.tagFilterPlaceholder')}
                    size="small"
                  />
                )}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* DataGrid */}
      {selectedForm && (
        <>
          <Paper sx={{ height: 400, width: '100%', mb: 2 }}>
            <DataGrid
              rows={dataGridRows}
              columns={columns}
              slots={{
                toolbar: CustomToolbar
              }}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
            />
          </Paper>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('cardReporting.userAnswersTitle')}
          </Typography>

          {/* Yeni: Kullanıcı arama alanı */}
          <TextField
            size="small"
            label={t('cardReporting.userSearch') || "Kullanıcı Ara"}
            placeholder={t('cardReporting.userSearchPlaceholder') || "E-posta veya kullanıcı adı girin"}
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ mb: 2, width: '100%', maxWidth: 900 }}
          />

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 1,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {loadingAnswers ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={1}>
                {finalUserGroups.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography>{t('cardReporting.noAnswersFound')}</Typography>
                  </Grid>
                ) : (
                  finalUserGroups.map((userObj, index) => (
                    <Grid item xs={12} key={index}>
                      <Card sx={{ position: 'relative' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => handleReject(userObj.responseDocId)}
                            >
                              {t('cardReporting.rejectButton')}
                            </Button>
                          </Box>

                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {t('cardReporting.userEmail')}: {userObj.userEmail}
                          </Typography>
                          {userObj.userTags?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {t('cardReporting.tags')}: {userObj.userTags.join(', ')}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {t('cardReporting.answerDate')}: {userObj.displayDate}
                          </Typography>

                          <Box sx={{ mt: 1 }}>
                            {userObj.answers.map((ans, ansIndex) => {
                              const questionPlainText = stripHtmlTags(ans.questionText || '')
                              const textIsImage = isLikelyImageUrl(ans.textAnswer)
                              const textIsVideo = isLikelyVideoUrl(ans.textAnswer)

                              return (
                                <Box
                                  key={ansIndex}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    border: '1px solid #eee',
                                    borderRadius: 1
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {t('cardReporting.question')}: {questionPlainText}
                                  </Typography>

                                  {/* Metin (normal) */}
                                  {ans.textAnswer && !textIsImage && !textIsVideo && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="body2">
                                        <strong>{t('cardReporting.answer')}:</strong>
                                      </Typography>
                                      <Box
                                        sx={{ mt: 0.5 }}
                                        dangerouslySetInnerHTML={{
                                          __html: ans.textAnswer
                                        }}
                                      />
                                    </Box>
                                  )}

                                  {/* Metin (resim linki) */}
                                  {ans.textAnswer &&
                                    textIsImage &&
                                    typeof ans.textAnswer === 'string' && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2">
                                          <strong>{t('cardReporting.answerImage')}:</strong>
                                        </Typography>
                                        <CardMedia
                                          component="img"
                                          src={ans.textAnswer}
                                          alt="Cevap Resim"
                                          sx={{
                                            maxWidth: 300,
                                            borderRadius: 1,
                                            mt: 0.5
                                          }}
                                        />
                                      </Box>
                                    )}

                                  {/* Metin (video linki) */}
                                  {ans.textAnswer &&
                                    textIsVideo &&
                                    typeof ans.textAnswer === 'string' && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2">
                                          <strong>{t('cardReporting.answerVideo')}:</strong>
                                        </Typography>
                                        <video
                                          width="320"
                                          height="240"
                                          controls
                                          style={{ borderRadius: '8px', marginTop: 4 }}
                                        >
                                          <source src={ans.textAnswer} type="video/mp4" />
                                          Tarayıcınız video etiketini desteklemiyor.
                                        </video>
                                      </Box>
                                    )}

                                  {/* mediaList => [{ type: 'image'|'video', url: string }] */}
                                  {ans.mediaList && ans.mediaList.length > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                        mt: 1
                                      }}
                                    >
                                      {ans.mediaList.map((mItem, mIdx) => {
                                        if (!mItem?.url) return null

                                        if (mItem.type === 'image') {
                                          return (
                                            <Box key={mIdx} sx={{ display: 'flex', flexDirection: 'column' }}>
                                              <CardMedia
                                                component="img"
                                                src={mItem.url}
                                                alt="Answer Media"
                                                sx={{ maxWidth: 200, borderRadius: 1 }}
                                              />
                                              <Button
                                                sx={{ mt: 0.5 }}
                                                onClick={() => {
                                                  const formName = sanitizeFileName(selectedForm?.name || 'Form')
                                                  const userName = sanitizeFileName(userObj.userEmail)
                                                  const questionNumber = ansIndex + 1

                                                  const extension = getFileExtension(mItem.url, 'image')
                                                  const fileName = `${formName}-${userName}-${questionNumber}.${extension}`

                                                  downloadWithCustomName(mItem.url, fileName)
                                                }}
                                              >
                                                {t('cardReporting.downloadSingle') || "İndir"}
                                              </Button>
                                            </Box>                                           
                                          )
                                        }

                                        if (mItem.type === 'video') {
                                          return (
                                            <Box key={mIdx}>
                                              <video
                                                width="200"
                                                height="auto"
                                                controls
                                                style={{ borderRadius: '8px' }}
                                              >
                                                <source src={mItem.url} type="video/mp4" />
                                                Tarayıcınız video etiketini desteklemiyor.
                                              </video>
                                              <Button
                                                onClick={() => {
                                                  const formName = sanitizeFileName(selectedForm?.name || 'Form')
                                                  const userName = sanitizeFileName(userObj.userEmail)
                                                  const extension = getFileExtension(mItem.url, 'video')
                                                  const fileName = `${formName}-${userName}-${ansIndex + 1}.${extension}`

                                                  downloadWithCustomName(mItem.url, fileName)
                                                }}
                                              >
                                                {t('cardReporting.downloadSingle') || "İndir"}
                                              </Button>
                                            </Box>
                                          )
                                        }

                                        return (
                                          <Typography variant="body2" key={mIdx}>
                                            {t('cardReporting.unsupportedMedia')}
                                          </Typography>
                                        )
                                      })}
                                    </Box>
                                  )}

                                  {/* Ayrı videoUrl alanı */}
                                  {ans.videoUrl && typeof ans.videoUrl === 'string' && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="body2">
                                        <strong>{t('cardReporting.videoAnswer')}:</strong>
                                      </Typography>
                                      <video
                                        width="320"
                                        height="240"
                                        controls
                                        style={{
                                          borderRadius: '8px',
                                          marginTop: 4
                                        }}
                                      >
                                        <source src={ans.videoUrl} type="video/mp4" />
                                        Tarayıcınız video etiketini desteklemiyor.
                                      </video>
                                      {(() => {
                                        const formName = sanitizeFileName(selectedForm?.name || 'Form')
                                        const userName = sanitizeFileName(userObj.userEmail)
                                        const extension = getFileExtension(ans.videoUrl, 'video')
                                        const fileName = `${formName}-${userName}-${ansIndex + 1}.${extension}`

                                        return (
                                          <Button
                                            component="a"
                                            href={ans.videoUrl}
                                            download={fileName}
                                            sx={{ mt: 0.5 }}
                                          >
                                            {t('cardReporting.downloadSingle') || "İndir"}
                                          </Button>
                                        )
                                      })()}
                                    </Box>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </Paper>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}